import type { FormHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface FormProps extends FormHTMLAttributes<HTMLFormElement> {
  children: ReactNode;
}

/**
 * Wrapper de formulaire générique.
 *
 * - Hors d'un Dialog : layout vertical par défaut (`space-y-6`).
 * - Dans un Dialog : ajouter `className="contents"` ou utiliser `<FormDialog>`
 *   qui gère lui-même la structure header/body/footer.
 */
export function Form({ children, className, ...props }: FormProps): JSX.Element {
  return (
    <form className={cn('space-y-6', className)} noValidate {...props}>
      {children}
    </form>
  );
}

interface FormSectionProps {
  title?: string;
  description?: string;
  children: ReactNode;
}

export function FormSection({ title, description, children }: FormSectionProps): JSX.Element {
  return (
    <section className="space-y-4">
      {(title || description) && (
        <header className="space-y-0.5 border-b border-border/60 pb-2">
          {title && (
            <h3 className="text-sm font-semibold leading-tight text-foreground">{title}</h3>
          )}
          {description && <p className="text-xs text-muted-foreground">{description}</p>}
        </header>
      )}
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </section>
  );
}

/** @deprecated Use `<DialogFooter>` directly inside `<DialogContent>` instead. */
interface FormActionsProps {
  children: ReactNode;
}

export function FormActions({ children }: FormActionsProps): JSX.Element {
  return <div className="flex items-center justify-end gap-2 pt-2">{children}</div>;
}
